// Lead Detail Page - Clean & Minimal

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLead, updateLead, deleteLead, getLeadFileSignedUrl, leadKeinSpam } from '@/lib/api';
import toast from 'react-hot-toast';
import { formatDate } from '@/lib/utils';
import { statusColors } from '@/lib/types';
import type { LeadStatus, LeadUpdateData } from '@/lib/types';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import DynamicFieldRenderer from '@/components/leads/DynamicFieldRenderer';

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  const { data: lead, isLoading, error } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => getLead(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (lead?.notes) {
      setNotes(lead.notes);
    }
  }, [lead]);

  const updateMutation = useMutation({
    mutationFn: (updates: LeadUpdateData) => updateLead(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Gespeichert');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Speichern');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLead(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['customer-leads'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Lead gelöscht');
      navigate('/leads');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Fehler beim Löschen');
    },
  });

  // Fehlgriff des Spamfilters zurückholen: Bewertung weg, Status „Neu" und —
  // falls noch nicht geschehen — nachträglich an den Betrieb weiterleiten.
  const keinSpamMutation = useMutation({
    mutationFn: () => leadKeinSpam(id!),
    onSuccess: (ergebnis) => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success(
        ergebnis === 'weitergeleitet'
          ? 'Zurückgeholt und an den Betrieb weitergeleitet'
          : 'Zurückgeholt (war bereits weitergeleitet)'
      );
    },
    onError: (error: any) => {
      toast.error(error.message || 'Zurückholen fehlgeschlagen');
    },
  });

  const handleStatusChange = (newStatus: LeadStatus) => {
    updateMutation.mutate({ status: newStatus });
  };

  const handleSaveNotes = () => {
    updateMutation.mutate({ notes: notes || undefined });
    setIsEditingNotes(false);
  };

  const handleDelete = () => {
    if (window.confirm('Möchten Sie diesen Lead wirklich löschen?')) {
      deleteMutation.mutate();
    }
  };

  // Extrahiere Datei-Infos aus fields
  const getFileInfo = () => {
    if (!lead?.fields) return null;
    
    const fileFields = ['dateinamen', 'dateiname', 'file', 'files', 'attachment', 'anhang'];
    const countFields = ['anzahl_dateien', 'file_count', 'dateien'];
    
    let fileNames: string[] = [];
    let fileCount = 0;
    
    for (const [key, value] of Object.entries(lead.fields)) {
      if (fileFields.includes(key.toLowerCase()) && value) {
        if (typeof value === 'string' && value.trim()) {
          fileNames = value.split(',').map(f => f.trim()).filter(Boolean);
        } else if (Array.isArray(value)) {
          fileNames = value;
        }
      }
      if (countFields.includes(key.toLowerCase()) && typeof value === 'number') {
        fileCount = value;
      }
    }
    
    if (fileNames.length > 0 || fileCount > 0) {
      return { fileNames, fileCount: fileCount || fileNames.length };
    }
    return null;
  };

  const fileInfo = getFileInfo();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-300 border-t-neutral-900"></div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-neutral-900 mb-2">Lead nicht gefunden</h2>
        <p className="text-neutral-500 mb-6">Der angeforderte Lead existiert nicht.</p>
        <Button onClick={() => navigate('/leads')}>Zurück zur Übersicht</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/leads')}
            className="text-sm text-neutral-500 hover:text-neutral-700 mb-2 flex items-center gap-1"
          >
            ← Zurück
          </button>
          <h1 className="text-2xl font-semibold text-neutral-900">{lead.contact_name}</h1>
          <p className="text-neutral-500">{lead.contact_email}</p>
        </div>

        <div className="flex items-center gap-2">
          <Select
            options={[
              { value: 'new', label: 'Neu' },
              { value: 'contacted', label: 'Kontaktiert' },
              { value: 'in_progress', label: 'In Bearbeitung' },
              { value: 'converted', label: 'Konvertiert' },
              { value: 'archived', label: 'Archiviert' },
            ]}
            value={lead.status}
            onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
          />
          
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            Löschen
          </Button>
        </div>
      </div>

      {/* Vom Spamfilter abgefangen: sagen, warum — und den Rückweg anbieten.
          Ohne diesen Knopf bliebe ein Fehlgriff für immer beim Betrieb unbekannt. */}
      {lead.outcome === 'spam' && lead.spam_grund && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm">
            <p className="font-medium text-red-800">Automatisch als Spam erkannt — nicht an den Betrieb weitergeleitet</p>
            <p className="text-red-700 mt-0.5">Grund: {lead.spam_grund}</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => keinSpamMutation.mutate()}
            disabled={keinSpamMutation.isPending}
          >
            {keinSpamMutation.isPending ? 'Wird weitergeleitet …' : 'Kein Spam – jetzt weiterleiten'}
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Meta Info */}
        <div className="space-y-4">
          {/* Status */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-neutral-500 mb-3">Status</h3>
            <Badge variant={lead.status}>
              {statusColors[lead.status].label}
            </Badge>

            {/* Was wurde daraus? – füttert die Lead-Qualitäts-Analyse im Analytics-Bereich */}
            <div className="mt-4">
              <p className="text-xs text-neutral-500 mb-1.5">Was wurde daraus?</p>
              <div className="flex flex-wrap gap-1.5">
                {([
                  ['won', '👍 Auftrag', 'border-green-300 text-green-700 bg-green-50'],
                  ['lost', 'Nichts geworden', 'border-gray-300 text-gray-600 bg-gray-50'],
                  ['spam', 'Spam', 'border-red-200 text-red-600 bg-red-50'],
                ] as Array<['won' | 'lost' | 'spam', string, string]>).map(([val, label, cls]) => (
                  <button
                    key={val}
                    onClick={() => updateMutation.mutate({ outcome: lead.outcome === val ? null : val })}
                    className={`px-2.5 py-1 rounded-md border text-xs font-medium transition ${
                      lead.outcome === val ? cls : 'border-neutral-200 text-neutral-400 hover:text-neutral-700 hover:border-neutral-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-neutral-500">Eingegangen</dt>
                <dd className="text-neutral-900">{formatDate(lead.created_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-500">Aktualisiert</dt>
                <dd className="text-neutral-900">{formatDate(lead.updated_at)}</dd>
              </div>
            </dl>
          </div>

          {/* Contact */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-neutral-500 mb-3">Kontakt</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-neutral-500">Name</dt>
                <dd className="text-neutral-900 font-medium">{lead.contact_name}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">E-Mail</dt>
                <dd>
                  <a href={`mailto:${lead.contact_email}`} className="text-blue-600 hover:text-blue-700">
                    {lead.contact_email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">Telefon</dt>
                <dd>
                  {lead.contact_phone ? (
                    <a href={`tel:${lead.contact_phone}`} className="text-blue-600 hover:text-blue-700">
                      {lead.contact_phone}
                    </a>
                  ) : (
                    <span className="text-neutral-400">-</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Source */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-neutral-500 mb-3">Quelle</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-neutral-500">Kunde</dt>
                <dd className="text-neutral-900">{lead.customer?.name || lead.customer_slug}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">Formular</dt>
                <dd className="text-neutral-900">{lead.form_identifier}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Right: Fields & Notes */}
        <div className="lg:col-span-2 space-y-4">
          {/* Fields */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-neutral-500 mb-4">Angaben</h3>
            <DynamicFieldRenderer
              fields={lead.fields}
              labels={lead.field_labels}
            />
          </div>

          {/* Files */}
          {((lead.files?.length ?? 0) > 0 || fileInfo) && (
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-neutral-500 mb-4">Dateien</h3>
              
              {/* Files from lead.files array */}
              {lead.files && lead.files.length > 0 && (
                <ul className="space-y-2">
                  {lead.files.map((file, idx) => (
                    <li key={idx}>
                      <button
                        onClick={async () => {
                          try {
                            const url = await getLeadFileSignedUrl(file.file_url);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } catch (e: any) {
                            toast.error(e.message || 'Datei konnte nicht geöffnet werden');
                          }
                        }}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {file.file_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Files from fields (without URL) */}
              {fileInfo && fileInfo.fileNames.length > 0 && !lead.files?.length && (
                <div>
                  <ul className="space-y-2">
                    {fileInfo.fileNames.map((fileName, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-neutral-700">
                        <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {fileName}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-neutral-400 mt-3">
                    Dateien wurden auf der Kundenwebsite hochgeladen. Kontaktieren Sie den Kunden für den Download.
                  </p>
                </div>
              )}

              {/* Only count, no names */}
              {fileInfo && fileInfo.fileNames.length === 0 && fileInfo.fileCount > 0 && (
                <p className="text-sm text-neutral-600">
                  {fileInfo.fileCount} {fileInfo.fileCount === 1 ? 'Datei' : 'Dateien'} angehängt
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-neutral-500">Notizen</h3>
              {!isEditingNotes && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingNotes(true)}
                >
                  Bearbeiten
                </Button>
              )}
            </div>

            {isEditingNotes ? (
              <div className="space-y-3">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-neutral-200 rounded-lg p-3 text-sm focus:ring-1 focus:ring-neutral-400 focus:border-neutral-400 focus:outline-none"
                  rows={4}
                  placeholder="Notizen eingeben..."
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNotes(lead.notes || '');
                      setIsEditingNotes(false);
                    }}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveNotes}
                    loading={updateMutation.isPending}
                  >
                    Speichern
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">
                {lead.notes || (
                  <span className="text-neutral-400 italic">Keine Notizen</span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
